import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { FadeIn, Stagger } from "@/components/ui/scroll-animated";
import { AutoBreakText } from "@/components/ui/text-utils";
import { useWorkProcessData } from "@/contexts/HomeDataContext";
import { useTranslations } from 'next-intl';
import { ProcessStep } from "@/types/homePage";

// Process Step Card Component
const ProcessStepCard: React.FC<{ step: ProcessStep; index: number }> = ({ step, index }) => {
  const t = useTranslations('WorkProcess.steps');

  return (
    <Stagger
      delay={400}
      staggerDelay={200}
      index={index}
      className="relative group"
    >
      <Card
        className={`${step.bgColor} border-0 rounded-[5px] w-[300px] h-[191px] transform transition-all duration-500 ease-out group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-blue-500/20 backdrop-blur-sm overflow-hidden relative`}
      >
        {/* Gradient overlay for better visual hierarchy */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <CardContent className="p-0 h-full flex flex-col justify-between relative">
          {/* Number Badge - Absolute positioned at top-left */}
          <div className="absolute top-0 left-0 z-20">
            <div className="w-10 h-10 rounded-tl-[5px] rounded-br-[5px] bg-brand-gradient flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110 shadow-lg">
              <span className="text-white text-sm font-bold">
                {step.number}
              </span>
            </div>
          </div>

          {/* Icon - Centered */}
          <div className="flex items-center justify-center flex-col gap-5 h-full">
            <div className="relative">
              <img
                className="w-[65px] h-[65px] transform transition-all duration-300 group-hover:scale-110 filter drop-shadow-lg"
                alt={`${t(`${step.id}.title`)} icon`}
                src={step.icon}
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
            </div>

            {/* Title and Description - Bottom */}
            <div className="text-center space-y-2 px-2">
              <h3>
                <AutoBreakText
                  text={t(`${step.id}.title`)}
                  className="text-white text-center font-space-grotesk text-[22px] font-bold leading-[36px] transition-all duration-300 group-hover:text-blue-100"
                  style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
                />
              </h3>
            </div>
          </div>


        </CardContent>
      </Card>
    </Stagger>
  );
};

export const WorkProcessSection = (): React.JSX.Element => {
  const data = useWorkProcessData();
  const t = useTranslations('WorkProcess');

  return (
    <section className="relative w-full bg-[#05092B] overflow-hidden -mt-16 md:-mt-20 lg:-mt-24">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Background image with overlay */}
        <div
          className="absolute inset-0 bg-[url('/svg/work-process/frame-behind.svg')] bg-cover bg-center bg-no-repeat animate-pulse-slow"
          style={{
            animation: 'float 20s ease-in-out infinite, pulse 8s ease-in-out infinite'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-48">
        {/* Spacing */}
        <div className="pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-20 lg:pb-24">
          {/* Header Section */}
          <header className="text-center mb-12 sm:mb-16 lg:mb-20">
            <FadeIn delay={200} className="mb-4 sm:mb-6">
              <span className="
                inline-block 
                px-4 py-2 
                bg-gradient-to-r from-blue-600/20 to-purple-600/20 
                border border-blue-500/30 
                rounded-full 
                text-blue-300 
                text-sm 
                sm:text-base 
                font-space-grotesk
                font-semibold 
                tracking-wider 
                uppercase
                backdrop-blur-sm
              ">
                {t('tagline')}
              </span>
            </FadeIn>

            <FadeIn delay={300}>
              <h1 className="
                text-3xl 
                sm:text-4xl 
                lg:text-5xl 
                xl:text-[60px] 
                font-space-grotesk
                font-bold 
                text-white 
                leading-tight
                max-w-4xl 
                mx-auto
                bg-gradient-to-r from-white via-blue-100 to-white 
                bg-clip-text 
                text-transparent
              ">
                <AutoBreakText text={t('heading')} />
              </h1>
            </FadeIn>
          </header>

          {/* Process Cards Grid with Curved Arrow Connectors */}
          <div className="flex justify-center px-4 lg:px-8">
            <div className="relative max-w-[1298px] w-full">
              {/* Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {data.steps.map((step: ProcessStep, index: number) => (
                  <div key={step.id} className="flex justify-center">
                    <ProcessStepCard step={step} index={index} />
                  </div>
                ))}
              </div>

              {/* Arrow Connectors - Desktop Only */}
              <div className="hidden lg:block">
                {/* Arrow 1: Card 1 to Card 2 */}
                <div className="absolute top-[220px] left-[120px] w-[280px] h-[62px] z-10">
                  <FadeIn delay={800}>
                    <img
                      src="/svg/work-process/arrow.svg"
                      alt="Process flow arrow"
                      className="w-full h-auto opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </FadeIn>
                </div>

                {/* Arrow 2: Card 2 to Card 3 */}
                <div className="absolute top-[220px] left-[calc(25%+120px)] w-[280px] h-[62px] z-10">
                  <FadeIn delay={900}>
                    <img
                      src="/svg/work-process/arrow.svg"
                      alt="Process flow arrow"
                      className="w-full h-auto opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </FadeIn>
                </div>

                {/* Arrow 3: Card 3 to Card 4 */}
                <div className="absolute top-[220px] left-[calc(50%+120px)] w-[280px] h-[62px] z-10">
                  <FadeIn delay={1000}>
                    <img
                      src="/svg/work-process/arrow.svg"
                      alt="Process flow arrow"
                      className="w-full h-auto opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </FadeIn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
